import React, { useEffect } from 'react'
import { EcomDataLayerHub } from '../../javascript/analytics/ecommerceDataLayerEventHub.services'
import { FetchEventDetailsOptions, IDateTimeEvent, IEpiProps, IEventRegistrationRefreshModel, IUserSelections, StepConstants } from '../models/event-reg-refresh-models'
import { useEventRegRefreshContext, useEventRegRefreshDispatch } from '../reducers/event-reg-refresh-reducers'
import { matchUserProgramToSpecialtyProgram } from '../helpers/helpers'
import { DateTimeStep } from './date-time-step'


interface IContextProps {
  epiProps: IEpiProps
  currentStep: string
  userSelections: IUserSelections
  eventRegistrationRefreshModel: IEventRegistrationRefreshModel
  hideAttendeesStep: boolean
  showOnlySharedAddOnProgramDateTimeSelection: boolean
  sharedAddOnProgramDateTimeEvent?: IDateTimeEvent | null
}

export const DateAndTimeStepAddOnProgram = () => {
  const { currentStep, epiProps, userSelections, eventRegistrationRefreshModel, hideAttendeesStep, showOnlySharedAddOnProgramDateTimeSelection, sharedAddOnProgramDateTimeEvent }: IContextProps = useEventRegRefreshContext()!
  const dispatch = useEventRegRefreshDispatch()!

  const addonNeedingDateTime = (userSelections?.userAddOnProgramSelections || []).find(a => !a.disableDateTimeSelection && !a.programSku)

  // If somehow this step is reached with only product-based add-ons, advance past it
  useEffect(() => {
    if (currentStep === StepConstants.DateTimeAddOnProgramStep && !addonNeedingDateTime) {
      dispatch({
        type: 'updateCurrentStep',
        data: hideAttendeesStep ? StepConstants.ReviewStep : StepConstants.AttendeesStep
      })
    }
  }, [currentStep, addonNeedingDateTime])

  if (!addonNeedingDateTime) {
    return null
  }

  const fetchAddOnEventDetails = async (options?: FetchEventDetailsOptions) => {
    // Only datesFilter is used for add-ons, other options are ignored
    const { datesFilter = [] } = options || {};
    // Get the specialty program selection's seat SKU to filter add-on events
    const specialtyEventSeatSku = userSelections?.userProgramDateTimeSelection?.seatSku || '';
    const addOnProgramCode = addonNeedingDateTime?.programCode || '';
    const specialtyProgramCode = userSelections?.userProgramSelection?.programCode || '';

    // Get specialty event end date for filtering add-on events
    const specialtyEndDate = userSelections?.userProgramDateTimeSelection?.endDate || '';

    // Build query parameters with filters
    const queryParams = new URLSearchParams({
      specialty: specialtyProgramCode,
      seatsku: specialtyEventSeatSku,
      qualification: addOnProgramCode,
      enddate: specialtyEndDate,
      dates: JSON.stringify(datesFilter.length > 0 ? datesFilter : []),
    })

    // Construct the URL with query parameters - to get add on program events
    const url = `/internalapi/EventRegistrationRefreshApi/AddOnProgramEventDetailsList?${queryParams.toString()}`

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`API call failed with status: ${response.status}`)
    }

    return await response.json();
  };

  // Handle event selection
  const handleSelectEvent = (event: IDateTimeEvent) => {
    // Check if this event is currently selected
    const isCurrentlySelected = userSelections.userAddOnProgramDateTimeSelection?.eventNumber === event.eventNumber

    if (isCurrentlySelected) {
      // Deselect - clear the event selection
      dispatch({
        type: 'updateUserSelections',
        data: {
          ...userSelections,
          userAddOnProgramDateTimeSelection: null,
          attendees: []
        }
      })
    } else {
      // Select new event
      dispatch({
        type: 'updateUserSelections',
        data: {
          ...userSelections,
          userAddOnProgramDateTimeSelection: event,
          attendees: []
        }
      })
    }
    
    // Reset steps after this step regardless of select/deselect
    dispatch({
      type: 'resetStepsAfter',
      data: StepConstants.DateTimeAddOnProgramStep
    })
  };

  // Check if an event is currently selected
  const isEventSelected = (eventNumber: string): boolean => {
    return userSelections.userAddOnProgramDateTimeSelection?.eventNumber === eventNumber
  };

  // Check if any event is selected for validation
  const isEventSelectedForValidation = (): boolean => {
    return !!userSelections.userAddOnProgramDateTimeSelection
  };

  // Check if we should show only shared selection
  const shouldShowOnlyShared = (): boolean => {
    return showOnlySharedAddOnProgramDateTimeSelection;
  };

  // Called if user clicks 'show all programs' CTA in the shared selection view (where only shared card is visible)
  const handleShowAllPrograms = () => {
    dispatch({ type: 'setShowOnlySharedAddOnProgramDateTimeSelection', data: false });
  };

  // Handle continue button click
  const handleContinue = () => {
    // Check if an event is selected
    if (userSelections.userAddOnProgramDateTimeSelection) {
      EcomDataLayerHub.selectSession(
        userSelections.userAddOnProgramDateTimeSelection,
        userSelections.userAddOnProgramDateTimeSelection.isBlended.toString()
      )
      // Check if hideAttendeesStep is true - if so, go to review instead of attendees
      const nextStep = hideAttendeesStep
        ? StepConstants.ReviewStep
        : StepConstants.AttendeesStep;

      // Navigate to the appropriate next step
      dispatch({
        type: 'updateCurrentStep',
        data: nextStep
      })
    }
  };

  if (currentStep == StepConstants.DateTimeAddOnProgramStep) {
    const selectedSpecialtyProgram = matchUserProgramToSpecialtyProgram(
      userSelections.userProgramSelection,
      eventRegistrationRefreshModel
    )

    return (
      <DateTimeStep
        fetchEventDetails={fetchAddOnEventDetails}
        handleEventSelection={handleSelectEvent}
        handleContinue={handleContinue}
        isEventSelected={isEventSelected}
        isEventSelectedForValidation={isEventSelectedForValidation}
        title={epiProps.selectDateAndTimeStepAddOnProgramTitle}
        description={epiProps.selectDateAndTimeStepAddOnProgramDescription}
        validationMessage={epiProps.selectDateAndTimeStepAddOnProgramValidationMessage}
        currentStep={currentStep}
        targetStep={StepConstants.DateTimeAddOnProgramStep}
        programName={addonNeedingDateTime?.programName}
        dateTimeStepIntroMessage={selectedSpecialtyProgram?.dateTimeStepIntroMessage}
        shouldShowOnlyShared={shouldShowOnlyShared()}
        handleShowAllPrograms={handleShowAllPrograms}
        sharedDateTimeEvent={sharedAddOnProgramDateTimeEvent}
      />
    )
  }

  return null
}